import {useExistingExternalIdsLazyQuery} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {ImportParseError, ImportPreviewList} from 'src/ImportPreview';
import {Textarea} from 'src/inputs/Textarea';
import parseEzpass from 'src/parseEzpass';
import {EZPASS_SOURCE} from 'src/parseEzpass';
import {parseJson} from 'src/parseJson';
import type {ExtractResult} from '@danwang/extractor';
import type Option from '@danwang/option';
import type {EzpassResult} from 'src/parseEzpass';

type Props = {
  onChange: (value: Option<EzpassResult[]>) => void;
};

export const ImportEzpass = (props: Props) => {
  const [text, setText] = React.useState('');
  const [parsed, setParsed] = React.useState<ExtractResult<EzpassResult[]>>(
    () => parseEzpass(text),
  );
  const [existingIds, setExistingIds] = React.useState<Set<string>>(new Set());
  const [checkExistingIds, {loading: checkingIds}] =
    useExistingExternalIdsLazyQuery();

  const handleChange = (value: string) => {
    const newParsed = parseEzpass(value);
    setText(value);
    setParsed(newParsed);
    props.onChange(newParsed.toOption());

    // Check which IDs already exist
    newParsed.toOption().forEach((transactions) => {
      const externalIds = transactions.map((t) => t.externalId);
      if (externalIds.length > 0) {
        checkExistingIds({
          variables: {externalSource: EZPASS_SOURCE, externalIds},
        }).then((result) => {
          const existing = result.data?.existingExternalIds ?? [];
          setExistingIds(new Set(existing));
        });
      } else {
        setExistingIds(new Set());
      }
    });
  };

  const handleFormat = () => {
    parseJson(text)
      .map((json) => JSON.stringify(json, null, 2))
      .forEach((formatted) => setText(formatted));
  };

  let preview: React.ReactNode = null;
  if (parsed.isSuccess) {
    preview = (
      <ImportPreviewList
        rows={parsed.successValue.map((t) => ({
          id: t.externalId,
          date: t.date,
          description: t.description,
          amount: t.amount,
        }))}
        existingIds={existingIds}
        checkingExisting={checkingIds}
      />
    );
  } else if (text.trim() !== '') {
    // An empty textarea trivially fails to parse; that is the resting state,
    // not an error worth surfacing.
    preview = (
      <ImportParseError sourceName="E-ZPass" error={parsed.failureValue} />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <Textarea
          value={text}
          onChange={handleChange}
          placeholder="Paste the JSON transaction history from your E-ZPass NY account…"
          spellCheck={false}
          className="h-36 resize-y font-mono text-xs"
        />
        <div className="flex justify-end">
          <Button
            variant="outline"
            className="px-3 py-1 text-sm"
            onClick={handleFormat}
            disabled={text.trim() === ''}
          >
            Format JSON
          </Button>
        </div>
      </div>
      {preview}
    </div>
  );
};
